/**
 * Line corrections by sales (end-user review F08, 11 Oct 2026).
 *
 * Sales can now finish a draft inside I2Q: correct the quantity or unit of a line, take a
 * line out that is not one, add a line the engine missed, and undo each of these. Every
 * change is one versioned, audited write to the app DB (`useLineOverride`, `useAddLine`,
 * `useRemoveAddedLine`); the engine's own reading stays visible next to the reviewed
 * values. After a change the case is read again, so prices, checks and the quotation
 * preview follow, and a reply prepared before is marked as made before the change.
 * Nothing is sent and nothing reaches F&O.
 *
 * Used in the line table and in the phone cards (the same columns), and below the lines.
 */

import { Icon, PencilLine, Plus, RotateCcw, Trash2 } from "@norican/icons";
import { Badge, Button, Dialog, Field, Input, Typography, useToastManager } from "@norican/react";
import { useState } from "react";

import {
  type LineOverride,
  useAddLine,
  useLineOverride,
  useLineQuantities,
  useRemoveAddedLine,
  workFailureText,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { type QuoteLine } from "@/lib/mock";
import { ACTION_LABELS, DRAFT_CELL_WORDS, SAVE_TOASTS } from "@/lib/sales-flow";
import { useDialogUsage } from "@/lib/usage-react";
import { formatQty } from "@/lib/work-labels";

/** Key prefix of a line sales added (never an engine line). */
export const ADDED_KEY_PREFIX = "add-";

/** What a quantity box says for anything that is not a whole number above 0. */
export const QTY_WHOLE_ERROR = "Enter a whole number above 0.";

/** True for a line sales added. */
export function isAddedLine(line: Pick<QuoteLine, "lineKey" | "override">): boolean {
  return line.override?.kind === "add" || (line.lineKey ?? "").startsWith(ADDED_KEY_PREFIX);
}

/** "5 box", "5", "box" or "" for a quantity and a unit. */
export function amountText(qty: number | null | undefined, unit: string | null | undefined) {
  return [qty == null ? "" : String(qty), (unit ?? "").trim()].filter(Boolean).join(" ");
}

/**
 * The quantity a person typed, as a whole number above zero; null otherwise. I2Q holds
 * whole quantities (end-user review F02): "1.5" is not rounded, it is refused.
 */
export function wholeQuantity(input: string): number | null {
  const value = input.trim().replace(/\s/g, "");
  if (!/^\d{1,7}$/.test(value)) {
    return null;
  }
  const qty = Number.parseInt(value, 10);
  return qty > 0 && qty <= 1_000_000 ? qty : null;
}

/** The sentence under a corrected line: what sales changed and what I2Q read. */
export function overrideSentence(override: LineOverride | null | undefined): string | null {
  if (!override) {
    return null;
  }
  const changed = override.engine_changed
    ? " I2Q reads this line differently since then. Check it."
    : "";
  if (override.kind === "add") {
    return "Added by sales.";
  }
  if (override.kind === "remove") {
    return `Taken out of the quotation by sales.${changed}`;
  }
  if (override.qty == null && override.unit_price != null) {
    // A price set by hand, the quantity untouched (round 5): the sentence names the price.
    const found = [override.engine_unit_price, override.engine_currency].filter(Boolean).join(" ");
    return `Price set by sales. I2Q found ${found || "no price"}.${changed}`;
  }
  const engine = amountText(override.engine_qty, override.engine_unit);
  return `Corrected by sales. I2Q read ${engine || "no quantity"}.${changed}`;
}

/** The badge on a line sales corrected or added. */
export function CorrectionBadge({ override }: { override: LineOverride | null | undefined }) {
  if (!override || override.kind === "remove") {
    return null;
  }
  // A price set by hand with the quantity untouched is not a corrected quantity (round 5).
  const priceOnly = override.qty == null && override.unit_price != null;
  return (
    <Badge
      tone={override.engine_changed ? "warning" : "accent"}
      variant="outline"
      size="xs"
      className="w-fit"
      data-line-correction={override.kind}
    >
      {override.kind === "add"
        ? "Added by sales"
        : priceOnly
          ? "Price set by sales"
          : "Corrected by sales"}
    </Badge>
  );
}

/** The quantity cell: the reviewed quantity, what I2Q read, and the button to correct it. */
export function QtyCell({
  caseId,
  line,
  lineIndex,
}: {
  caseId: string;
  line: QuoteLine;
  lineIndex: number;
}) {
  const [open, setOpen] = useState(false);
  const override = line.override ?? null;
  const added = isAddedLine(line);
  const canCorrect = Boolean(line.lineKey) && (added || lineIndex >= 0);
  return (
    <span
      className="flex flex-col items-end gap-0.5"
      data-qty-issue={line.qtyIssue ? "" : undefined}
    >
      {line.qty == null ? (
        // The same words as the Qty cell of the table (U-05, 12 Oct 2026).
        <span className="text-warning-fg text-xs" title="No quantity in the mail" data-qty-missing>
          {DRAFT_CELL_WORDS.qtyMissing}
        </span>
      ) : (
        // Never rounded (F02); a quantity or unit to check is marked (F03).
        <span className="tabular-nums" data-reviewed={override?.kind === "edit" || undefined}>
          {formatQty(line.qty)}
        </span>
      )}
      {line.qty != null && line.qtyIssue ? (
        <span className="text-warning-fg text-xs" title={line.qtyIssue}>
          check unit
        </span>
      ) : line.qty != null && line.qtyConversion && line.priceQty != null ? (
        <span className="text-fg-muted text-xs tabular-nums" title={line.qtyConversion}>
          = {formatQty(line.priceQty)} {line.price?.uom ?? ""}
        </span>
      ) : null}
      {override?.kind === "edit" && (
        <span className="text-fg-subtle text-xs" data-engine-qty>
          I2Q read {amountText(override.engine_qty, override.engine_unit) || "none"}
        </span>
      )}
      {canCorrect && (
        <Button
          size="sm"
          variant="ghost"
          tone="neutral"
          startIcon={<Icon icon={PencilLine} size="xs" />}
          aria-label={`Correct quantity or unit of: ${line.enquiryText}`}
          onClick={() => {
            logger.debug("[case] open line correction", { line: line.id, added });
            setOpen(true);
          }}
        >
          Correct
        </Button>
      )}
      {open &&
        (added ? (
          <AddLineDialog caseId={caseId} line={line} open onOpenChange={setOpen} />
        ) : (
          <LineCorrectionDialog
            caseId={caseId}
            line={line}
            lineIndex={lineIndex}
            open
            onOpenChange={setOpen}
          />
        ))}
    </span>
  );
}

function ErrorText({ error }: { error: unknown }) {
  const message = workFailureText(error, "line");
  return message ? (
    <Typography variant="body-sm" className="text-danger-fg" role="alert">
      {message}
    </Typography>
  ) : null;
}

/**
 * True when "Save" would change nothing: the quantity, the unit and the note in the boxes
 * are the ones the line carries. An empty quantity box keeps the quantity of the line.
 */
export function correctionUnchanged(
  line: Pick<QuoteLine, "qty" | "unit" | "override">,
  typed: { qty: number | null; unit: string; note: string },
): boolean {
  const sameQty = typed.qty === null || typed.qty === line.qty;
  const sameUnit = typed.unit.trim() === (line.unit ?? "").trim();
  const sameNote = typed.note.trim() === (line.override?.note ?? "").trim();
  return sameQty && sameUnit && sameNote;
}

/**
 * Correct the quantity or unit of an engine line, take it out, or undo the correction.
 *
 * Ease round, 13 Oct 2026: the quantity is selected when the box takes the focus (typing
 * 7 over 1 gave 17), Enter in a box saves, "Not a line to quote" sits away from "Save",
 * the cell shows the new value at once, the toast names the line and the value and
 * carries "Undo", and a save that changes nothing says "No change" and sends nothing.
 * `position` is the row number the page shows; `unassigned` says nobody has the case, so
 * the toast of this save also says whose it is now.
 */
export function LineCorrectionDialog({
  caseId,
  line,
  lineIndex,
  open,
  onOpenChange,
  position,
  unassigned = false,
}: {
  caseId: string;
  line: QuoteLine;
  lineIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position?: number;
  unassigned?: boolean;
}) {
  const toast = useToastManager();
  const save = useLineOverride(caseId);
  const quantities = useLineQuantities(caseId);
  // The usage log: opened, saved, or closed without a save. Never a value of the form.
  const usage = useDialogUsage("correct_quantity", open, caseId);
  const override = line.override ?? null;
  // A part of a unit read by I2Q (1.5 kg, F02) is not put in the box: a correction takes a
  // whole number, and an empty box keeps I2Q's exact quantity when only the unit changes.
  const [qtyText, setQtyText] = useState(
    line.qty == null || !Number.isInteger(line.qty) ? "" : String(line.qty),
  );
  const [unit, setUnit] = useState(line.unit ?? "");
  const [note, setNote] = useState(override?.note ?? "");
  const qty = wholeQuantity(qtyText);
  const qtyInvalid = qtyText.trim() !== "" && qty === null;
  const nothing = qty === null && unit.trim() === "";
  const lineKey = line.lineKey ?? "";
  const taken = unassigned ? ` ${SAVE_TOASTS.taken}` : "";

  // "Undo" of a saved quantity: the quantity sales had set before, or what I2Q read.
  const former =
    override?.kind === "edit" && override.qty != null
      ? { qty: override.qty, unit: override.unit ?? undefined }
      : null;
  const undo = () => {
    logger.debug("[case] line correction undo", { caseId, lineIndex, former: Boolean(former) });
    quantities.mutate(
      former
        ? { kind: "set", line_keys: [lineKey], qty: former.qty, unit: former.unit }
        : { kind: "restore", line_keys: [lineKey] },
    );
  };

  const send = (kind: "edit" | "remove" | "clear", done: string) => {
    if (kind === "edit" && correctionUnchanged(line, { qty, unit, note })) {
      // Nothing to send: say so, where a silent close read as a failed save.
      toast.add({
        title: SAVE_TOASTS.noChange.title,
        description: SAVE_TOASTS.noChange.text,
        type: "success",
      });
      onOpenChange(false);
      return;
    }
    const body =
      kind === "edit"
        ? {
            qty: qty ?? undefined,
            unit: unit.trim() || undefined,
            note: note.trim() || undefined,
          }
        : kind === "remove"
          ? { note: note.trim() || undefined }
          : {};
    logger.debug("[case] line correction", { caseId, lineIndex, kind });
    save.mutate(
      { lineIndex, line_key: lineKey, kind, ...body },
      {
        onSuccess: () => {
          if (kind === "edit") {
            toast.add({
              title: SAVE_TOASTS.quantity.title,
              description:
                SAVE_TOASTS.quantity.text(
                  position ?? lineIndex + 1,
                  amountText(qty ?? line.qty, unit.trim() || line.unit) || "no quantity",
                ) + taken,
              type: "success",
              actionProps: { children: ACTION_LABELS.undo, onClick: undo },
            });
          } else {
            toast.add({ title: done, description: CHANGED_NOTE + taken, type: "success" });
          }
          usage.saved();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          {/* A form, so Enter in a box saves. `contents`: the popup lays out its parts. */}
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault();
              if (!qtyInvalid && !nothing && !save.isPending) {
                send("edit", "");
              }
            }}
          >
            <Dialog.Header>
              <Dialog.Title>Correct this line</Dialog.Title>
              <Dialog.CloseButton />
            </Dialog.Header>
            <Dialog.Body className="flex flex-col gap-4">
              <Dialog.Description>
                {/* A span: the description is a paragraph already. */}
                <Typography as="span" variant="body-sm" tone="muted">
                  I2Q uses your values for the prices, the checks, the quotation preview and the
                  reply. What I2Q read stays visible. Nothing is sent.
                </Typography>
              </Dialog.Description>
              <div className="border-border-subtle rounded-md border px-3 py-2">
                <Typography variant="caption" tone="subtle" className="block">
                  Customer asked
                </Typography>
                <Typography
                  as="span"
                  variant="body-sm"
                  className="text-fg font-semibold break-words"
                >
                  &ldquo;{line.enquiryText}&rdquo;
                </Typography>
                <Typography variant="caption" tone="subtle" className="mt-1 block">
                  I2Q read{" "}
                  {amountText(
                    override?.kind === "edit" ? override.engine_qty : line.qty,
                    override?.kind === "edit" ? override.engine_unit : line.unit,
                  ) || "no quantity"}
                </Typography>
              </div>
              <Field
                label="Quantity"
                hint="A whole number. For a fraction of a unit, finish this line in F&O."
                error={qtyInvalid ? QTY_WHOLE_ERROR : undefined}
              >
                <Input
                  value={qtyText}
                  inputMode="numeric"
                  onChange={(event) => setQtyText(event.target.value)}
                  // The old value is selected: the first key replaces it.
                  onFocus={(event) => event.target.select()}
                  aria-label="Quantity"
                  autoFocus
                />
              </Field>
              <Field label="Unit" hint="As the customer wrote it, for example pcs, box or set.">
                <Input
                  value={unit}
                  maxLength={16}
                  onChange={(event) => setUnit(event.target.value)}
                  aria-label="Unit"
                />
              </Field>
              <Field label="Note" hint="Optional, for colleagues. For example: 5 boxes in the PDF.">
                <Input
                  value={note}
                  maxLength={300}
                  onChange={(event) => setNote(event.target.value)}
                  aria-label="Note"
                />
              </Field>
              <ErrorText error={save.error} />
            </Dialog.Body>
            <Dialog.Footer className="flex flex-wrap justify-end gap-2">
              {/* On the left, away from "Save": it takes the line out of the quotation. */}
              <Button
                variant="outline"
                tone="danger"
                className="mr-auto"
                loading={save.isPending && save.variables?.kind === "remove"}
                startIcon={<Icon icon={Trash2} size="sm" />}
                onClick={() => send("remove", "Line taken out of the quotation")}
              >
                Not a line to quote
              </Button>
              {override && (
                <Button
                  variant="outline"
                  tone="neutral"
                  loading={save.isPending && save.variables?.kind === "clear"}
                  startIcon={<Icon icon={RotateCcw} size="sm" />}
                  onClick={() => send("clear", "Correction taken back")}
                >
                  Use what I2Q read
                </Button>
              )}
              <Button
                type="submit"
                tone="accent"
                loading={save.isPending && save.variables?.kind === "edit"}
                disabled={qtyInvalid || nothing}
              >
                Save
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const CHANGED_NOTE = "I2Q reads the prices and the checks again. Prepare an earlier reply again.";

/** Add a line the engine missed, or change or delete one sales added (`line` given). */
export function AddLineDialog({
  caseId,
  line,
  open,
  onOpenChange,
}: {
  caseId: string;
  line?: QuoteLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToastManager();
  const add = useAddLine(caseId);
  const remove = useRemoveAddedLine(caseId);
  // The usage log: opened, saved, or closed without a save. Never a value of the form.
  const usage = useDialogUsage("add_line", open, caseId);
  const [text, setText] = useState(line?.enquiryText ?? "");
  const [qtyText, setQtyText] = useState(line?.qty == null ? "1" : String(line.qty));
  const [unit, setUnit] = useState(line?.unit ?? "");
  const [item, setItem] = useState(line?.decision?.item ?? "");
  const qty = wholeQuantity(qtyText);
  const qtyInvalid = qty === null;
  const textMissing = text.trim() === "";
  const editing = Boolean(line?.lineKey);

  const onSave = () => {
    if (qty === null || textMissing) {
      return;
    }
    logger.debug("[case] add line", { caseId, editing });
    add.mutate(
      {
        ...(editing ? { line_key: line?.lineKey ?? undefined } : {}),
        enquiry_line: text.trim(),
        qty,
        unit: unit.trim() || undefined,
        item: item.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.add({
            title: editing ? "Added line changed" : "Line added",
            description: CHANGED_NOTE,
            type: "success",
          });
          usage.saved();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Header>
            <Dialog.Title>{editing ? "Change the added line" : "Add a missed line"}</Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            <Dialog.Description>
              <Typography variant="body-sm" tone="muted">
                For a line the customer asked for and I2Q did not read. I2Q prices and checks it
                like the other lines. Nothing is sent.
              </Typography>
            </Dialog.Description>
            <Field
              label="What the customer asked for"
              required
              error={textMissing ? "Enter the line as the customer wrote it." : undefined}
            >
              <Input
                value={text}
                maxLength={500}
                onChange={(event) => setText(event.target.value)}
                aria-label="What the customer asked for"
                autoFocus
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Quantity"
                required
                error={qtyInvalid ? "Enter a whole number above 0." : undefined}
              >
                <Input
                  value={qtyText}
                  inputMode="numeric"
                  onChange={(event) => setQtyText(event.target.value)}
                  aria-label="Quantity"
                />
              </Field>
              <Field label="Unit">
                <Input
                  value={unit}
                  maxLength={16}
                  onChange={(event) => setUnit(event.target.value)}
                  aria-label="Unit"
                />
              </Field>
            </div>
            <Field
              label="Item number"
              hint="Optional. It must be in the catalogue of this area; without it the line stays open."
            >
              <Input
                value={item}
                maxLength={80}
                onChange={(event) => setItem(event.target.value)}
                aria-label="Item number"
              />
            </Field>
            <ErrorText error={add.error ?? remove.error} />
          </Dialog.Body>
          <Dialog.Footer className="flex flex-wrap justify-end gap-2">
            {editing && (
              <Button
                variant="outline"
                tone="danger"
                loading={remove.isPending}
                startIcon={<Icon icon={Trash2} size="sm" />}
                onClick={() =>
                  remove.mutate(
                    { line_key: line?.lineKey ?? "" },
                    {
                      onSuccess: () => {
                        toast.add({
                          title: "Added line deleted",
                          description: CHANGED_NOTE,
                          type: "success",
                        });
                        usage.saved();
                        onOpenChange(false);
                      },
                    },
                  )
                }
              >
                Delete this line
              </Button>
            )}
            <Button variant="outline" tone="neutral" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              tone="accent"
              loading={add.isPending}
              disabled={qtyInvalid || textMissing}
              onClick={onSave}
            >
              {editing ? "Save" : "Add line"}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** "Add a missed line" with its dialog. */
export function AddLineButton({ caseId }: { caseId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        tone="neutral"
        startIcon={<Icon icon={Plus} size="xs" />}
        onClick={() => setOpen(true)}
      >
        Add a missed line
      </Button>
      {open && <AddLineDialog caseId={caseId} open onOpenChange={setOpen} />}
    </>
  );
}

/**
 * The lines sales took out of the quotation, each with "Put back". `lines` are all lines
 * of the case in stored order (the index is what the API expects).
 */
export function RemovedLines({ caseId, lines }: { caseId: string; lines: readonly QuoteLine[] }) {
  const restore = useLineOverride(caseId);
  const removed = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => line.override?.kind === "remove");
  if (removed.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1" data-removed-lines>
      <Typography variant="caption" tone="subtle">
        Taken out of the quotation by sales
      </Typography>
      <ul className="flex flex-col gap-1">
        {removed.map(({ line, index }) => (
          <li key={line.id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-fg-muted min-w-0 break-words line-through">
              {line.enquiryText}
            </span>
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              startIcon={<Icon icon={RotateCcw} size="xs" />}
              loading={restore.isPending && restore.variables?.lineIndex === index}
              onClick={() =>
                restore.mutate({ lineIndex: index, line_key: line.lineKey ?? "", kind: "clear" })
              }
            >
              Put back
            </Button>
          </li>
        ))}
      </ul>
      <ErrorText error={restore.error} />
    </div>
  );
}
